'use client';

/**
 * PopularTripsPicker — the seeded road-trip catalogue as photo cards.
 *
 *  • variant="shelf": three photo rows under the empty planner ("real trips
 *    to steal"), the same on phones and desktop, with one way to the rest:
 *    "See all". No carousel: a peeking second card clipped its own title
 *    and price, and arrows plus "See all" were two ways to do one thing.
 *    Pointing at a row (hover or keyboard focus) reports it, so the map can
 *    light that trip's line up.
 *  • variant="all": every trip as a searchable list; takes over the planner
 *    body (reachable mid-trip from the trip menu).
 *
 * The whole card is one button: photo, route, km, days and nights, and a
 * visible "Take this trip" pill — no hover needed, so touch works. While a
 * photo loads its slot shows the drawn route, never a grey block. Pointing
 * at a card draws its line on the big map (no thumbnail badge: at 44px it
 * read as a broken image).
 *
 * Taking a trip over one with two or more places asks first ("Replace your
 * current trip?") — a centred dialog on desktop, a bottom sheet in the thumb
 * zone on phones. A lone start (or an unstarted round trip) is swapped
 * straight away; the planner offers Undo.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PopularRouteSummary } from '../../utils/popularTripsApi';
import { cardImageUrl, filterRoutes, formatRouteFacts, pickHeroImage, routeTripFacts } from './plannerStart';
import RouteMapThumb from './RouteMapThumb';

interface PopularTripsPickerProps {
  variant: 'shelf' | 'all';
  /** null while loading; [] when the catalogue didn't load. */
  routes: PopularRouteSummary[] | null;
  loadingId: string | null;
  onTake: (route: PopularRouteSummary) => void;
  onRetry?: () => void;
  /** Places in the trip being planned — taking a trip over 2+ asks first. */
  currentStopCount: number;
  /** shelf: open the full list. */
  onSeeAll?: () => void;
  /** all: back to the planner. */
  onBack?: () => void;
  /** shelf: how many cards to show before "See all". */
  limit?: number;
  /** shelf: a card is pointed at (hover/focus); null when none. */
  onPreview?: (routeId: string | null) => void;
}

function ChevronGlyph({ dir, className }: { dir: 'left' | 'right'; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
    </svg>
  );
}

function SearchGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </svg>
  );
}

function InfoGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 7.5h.01" />
    </svg>
  );
}

function ArrowGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/**
 * The card photo. The drawn route fills the slot from the first paint (a
 * placeholder that says something true, not a grey block); the photo fades
 * in over it once decoded. A missing or broken photo leaves the drawn route.
 * The first few cards load eagerly at high priority; Wikimedia photos are
 * asked for at card size.
 */
function TripPhoto({
  route,
  eager = false,
  width = 500,
}: {
  route: PopularRouteSummary;
  eager?: boolean;
  width?: number;
}) {
  const hero = pickHeroImage(route.collage_images);
  const src = hero ? cardImageUrl(hero, width) : undefined;
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    // Cached images can finish before React attaches onLoad.
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) setLoaded(true);
  }, [src]);
  const showPhoto = !!src && !failed;
  return (
    <>
      <span
        className="block h-full w-full bg-tp-brand-soft"
        data-testid={showPhoto ? undefined : 'popular-trip-fallback'}
        aria-hidden="true"
      >
        <RouteMapThumb routes={[route]} tone="fallback" className="h-full w-full" />
      </span>
      {showPhoto && (
        // eslint-disable-next-line @next/next/no-img-element -- third-party (Wikimedia/our media) URLs, sized per card
        <img
          ref={imgRef}
          src={src}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          {...(eager ? ({ fetchPriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>) : {})}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`h-full w-full object-cover transition-[opacity,transform] duration-500 ease-out motion-safe:group-hover:scale-[1.04] motion-reduce:transition-none ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </>
  );
}

/** "Take this trip" — a visible pill on every card (touch has no hover);
 *  hover/focus fills it. The whole card is the button. */
function TakePill({ loading }: { loading: boolean }) {
  return (
    <span
      className={`ca-take-pill inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[14px] font-semibold transition-colors duration-150 ${
        loading
          ? 'bg-tp-brand text-white'
          : 'bg-tp-brand/[0.08] text-tp-brand group-hover:bg-tp-brand group-hover:text-white group-has-[:focus-visible]:bg-tp-brand group-has-[:focus-visible]:text-white'
      }`}
    >
      {loading ? (
        <>
          <span aria-hidden="true" className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" />
          Loading…
        </>
      ) : (
        <>
          Take this trip
          <ArrowGlyph className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:translate-x-0.5 motion-reduce:transition-none" />
        </>
      )}
    </span>
  );
}

type TakeHandler = (route: PopularRouteSummary, trigger: HTMLButtonElement) => void;
type PreviewHandler = ((routeId: string | null) => void) | undefined;

/** Shared button chrome: the card IS the button. */
const CARD_BUTTON =
  'flex h-full w-full text-left focus-visible:outline-none disabled:cursor-wait';

/** "971 km · 4 days · 3 nights", km in the strong colour. */
function Facts({ route, className = '' }: { route: PopularRouteSummary; className?: string }) {
  const f = routeTripFacts(route);
  const rest = formatRouteFacts({ km: null, days: f.days, nights: f.nights });
  if (f.km === null && !rest) return null;
  return (
    <span className={`block truncate text-[13px] leading-5 text-tp-muted ${className}`} data-testid="popular-trip-facts">
      {f.km !== null && <span className="font-semibold text-tp-ink">{f.km.toLocaleString('en-AU')} km</span>}
      {f.km !== null && rest && <span aria-hidden="true"> · </span>}
      {f.km !== null && rest && <span className="sr-only">, </span>}
      {rest}
    </span>
  );
}

/** List row for the full catalogue — readable at the planner's 420px width. */
function TripRow({
  route,
  loading,
  disabled,
  onTake,
  eager,
  onPreview,
}: {
  route: PopularRouteSummary;
  loading: boolean;
  disabled: boolean;
  onTake: TakeHandler;
  eager: boolean;
  onPreview?: PreviewHandler;
}) {
  const titleId = `trip-${route.id}-row-title`;
  return (
    <article
      aria-labelledby={titleId}
      data-testid="popular-trip-card"
      onMouseEnter={onPreview ? () => onPreview(route.id) : undefined}
      onMouseLeave={onPreview ? () => onPreview(null) : undefined}
      onFocus={onPreview ? () => onPreview(route.id) : undefined}
      onBlur={onPreview ? () => onPreview(null) : undefined}
      className="group overflow-hidden rounded-2xl border border-tp-line bg-white shadow-[0_1px_2px_rgba(28,25,23,0.05)] transition-[box-shadow,border-color] duration-200 hover:border-tp-line-strong hover:shadow-[0_8px_24px_-14px_rgba(28,25,23,0.4)] has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-tp-brand/25"
    >
      <button
        type="button"
        disabled={disabled}
        aria-busy={loading || undefined}
        aria-label={`Take this trip: ${route.name}`}
        onClick={(e) => onTake(route, e.currentTarget)}
        className={`${CARD_BUTTON} items-stretch`}
      >
        <span className="relative block min-h-[120px] w-[116px] flex-none overflow-hidden min-[400px]:w-[132px] [&>*]:absolute [&>*]:inset-0">
          <TripPhoto route={route} eager={eager} width={280} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col p-3">
          <span id={titleId} className="line-clamp-2 text-[15px] font-semibold leading-5 tracking-tight text-tp-ink">
            {route.name}
          </span>
          {route.from && route.to && (
            <span className="mt-1 truncate text-[13px] leading-5 text-tp-muted">
              {route.from} → {route.to}
            </span>
          )}
          <Facts route={route} />
          <span className="mt-auto block pt-2">
            <TakePill loading={loading} />
          </span>
        </span>
      </button>
    </article>
  );
}

/** "Replace your current trip?" — portalled to <body> so the planner's
 *  overflow/stacking can't clip it. Desktop: a centred dialog, Keep on the
 *  left, Replace (the primary) on the right. Phones: a bottom sheet with the
 *  buttons stacked in the thumb zone, Replace nearest the thumb. */
function ReplaceTripDialog({
  route,
  stopCount,
  onConfirm,
  onCancel,
}: {
  route: PopularRouteSummary;
  stopCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const keepRef = useRef<HTMLButtonElement | null>(null);
  const replaceRef = useRef<HTMLButtonElement | null>(null);
  const [shown, setShown] = useState(false);

  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    keepRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Tab') {
        // Two buttons: keep focus inside the dialog.
        const order = [keepRef.current, replaceRef.current].filter(Boolean) as HTMLElement[];
        const i = order.indexOf(document.activeElement as HTMLElement);
        e.preventDefault();
        const next = e.shiftKey ? (i <= 0 ? order.length - 1 : i - 1) : (i + 1) % order.length;
        order[next]?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onCancel]);

  if (typeof document === 'undefined') return null;
  const where = route.from && route.to ? `${route.from} → ${route.to}` : null;
  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-end justify-center sm:items-center sm:p-4">
      <div
        className={`absolute inset-0 bg-tp-ink/50 transition-opacity duration-200 motion-reduce:transition-none ${shown ? 'opacity-100' : 'opacity-0'}`}
        onClick={onCancel}
        aria-hidden="true"
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="replace-trip-title"
        aria-describedby="replace-trip-body"
        className={`relative w-full overflow-hidden rounded-t-2xl bg-white px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-3 shadow-2xl transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none sm:max-w-[400px] sm:rounded-2xl sm:p-6 ${
          shown ? 'translate-y-0 sm:scale-100 sm:opacity-100' : 'translate-y-full sm:translate-y-0 sm:scale-[0.97] sm:opacity-0'
        }`}
      >
        {/* Grab handle: says "sheet" on phones. */}
        <span aria-hidden="true" className="mx-auto mb-4 block h-1 w-10 rounded-full bg-tp-line-strong sm:hidden" />
        <h2 id="replace-trip-title" className="text-[20px] font-semibold leading-7 tracking-tight text-tp-ink">
          Replace your current trip?
        </h2>
        <p id="replace-trip-body" className="mt-2 text-[15px] leading-6 text-tp-muted">
          <span className="font-semibold text-tp-ink">{route.name}</span>
          {where ? ` (${where})` : ''} takes the place of the {stopCount} stops you’re planning. Saved trips stay as
          they are.
        </p>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:grid sm:grid-cols-2">
          <button
            ref={keepRef}
            type="button"
            onClick={onCancel}
            className="h-12 min-h-0 rounded-lg bg-white px-4 ring-1 ring-inset ring-tp-line-strong text-[16px] font-semibold text-tp-ink transition-colors hover:bg-tp-surface focus:outline-none focus-visible:ring-4 focus-visible:ring-tp-brand/25 sm:h-11 sm:text-[15px]"
          >
            Keep my trip
          </button>
          <button
            ref={replaceRef}
            type="button"
            onClick={onConfirm}
            className="h-12 min-h-0 rounded-lg bg-tp-brand px-4 text-[16px] font-semibold text-white transition-colors hover:bg-tp-brand-ink focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-tp-brand/25 sm:h-11 sm:text-[15px]"
          >
            Replace trip
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function PopularTripsPicker({
  variant,
  routes,
  loadingId,
  onTake,
  onRetry,
  currentStopCount,
  onSeeAll,
  onBack,
  limit = 3,
  onPreview,
}: PopularTripsPickerProps) {
  const [filter, setFilter] = useState('');
  const [pending, setPending] = useState<PopularRouteSummary | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const take = useCallback(
    (route: PopularRouteSummary, trigger: HTMLButtonElement) => {
      triggerRef.current = trigger;
      // A lone start (or an unstarted round trip) isn't worth a dialog: swap
      // it straight away; the planner offers Undo.
      if (currentStopCount >= 2) {
        setPending(route);
        return;
      }
      onTake(route);
    },
    [currentStopCount, onTake]
  );

  const closeDialog = useCallback(() => {
    setPending(null);
    // Back to the card that asked, so keyboard users don't lose their place.
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const confirm = useCallback(() => {
    const route = pending;
    setPending(null);
    if (route) onTake(route);
  }, [pending, onTake]);

  const shown = useMemo(() => {
    if (!routes) return null;
    return variant === 'shelf' ? routes.slice(0, limit) : filterRoutes(routes, filter);
  }, [routes, variant, limit, filter]);

  const failed = routes !== null && routes.length === 0;
  const busy = loadingId !== null;

  const errorState = (
    <div className="rounded-xl border border-dashed border-tp-line-strong bg-tp-surface px-4 py-5 text-center">
      <p className="text-[13px] text-tp-muted">Popular road trips didn’t load just now.</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 text-[13px] font-semibold text-tp-brand underline-offset-2 hover:underline"
        >
          Try again
        </button>
      )}
    </div>
  );

  const dialog = pending && (
    <ReplaceTripDialog
      route={pending}
      stopCount={currentStopCount}
      onConfirm={confirm}
      onCancel={closeDialog}
    />
  );

  if (variant === 'shelf') {
    const more = shown && routes && routes.length > shown.length && onSeeAll ? routes.length : 0;
    return (
      <section aria-labelledby="popular-shelf-title" data-testid="popular-trips-shelf">
        {/* Title left; "See all" and (mouse only) arrows right — so the way
            into the full list is above the fold on a phone, not under it. */}
        <div className="flex items-center justify-between gap-3">
          <h2 id="popular-shelf-title" className="min-w-0 flex-1 truncate text-[20px] font-semibold leading-7 tracking-tight text-tp-ink">
            Or take a popular trip
          </h2>
          {more > 0 && (
            <button
              type="button"
              onClick={onSeeAll}
              className="-mr-2 inline-flex h-9 min-h-0 flex-none items-center gap-0.5 whitespace-nowrap rounded-lg px-2 text-[14px] font-semibold text-tp-brand transition-colors hover:bg-tp-brand/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/40"
              aria-label={`See all ${more} road trips`}
            >
              See all {more}
              <ChevronGlyph dir="right" className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-[14px] leading-5 text-tp-muted">Real routes, camps on the way.</p>
        {failed ? (
          <div className="mt-3">{errorState}</div>
        ) : (
          <ul
            aria-label="Popular road trips"
            className="mt-3 space-y-2.5"
            onMouseLeave={onPreview ? () => onPreview(null) : undefined}
          >
            {shown === null
              ? [0, 1, 2].map((i) => (
                  <li key={i} aria-hidden="true" className="flex h-[120px] overflow-hidden rounded-2xl border border-tp-line bg-white">
                    <div className="w-[116px] flex-none animate-pulse bg-tp-brand-soft motion-reduce:animate-none min-[400px]:w-[132px]" />
                    <div className="flex-1 space-y-2 p-3">
                      <div className="h-4 w-3/4 animate-pulse rounded bg-tp-line motion-reduce:animate-none" />
                      <div className="h-3.5 w-1/2 animate-pulse rounded bg-tp-surface motion-reduce:animate-none" />
                      <div className="mt-3 h-9 w-32 animate-pulse rounded-lg bg-tp-surface motion-reduce:animate-none" />
                    </div>
                  </li>
                ))
              : shown.map((r, i) => (
                  <li key={r.id}>
                    <TripRow
                      route={r}
                      eager={i < 3}
                      loading={loadingId === r.id}
                      disabled={busy}
                      onTake={take}
                      onPreview={onPreview}
                    />
                  </li>
                ))}
          </ul>
        )}
        {dialog}
      </section>
    );
  }

  return (
    <section aria-labelledby="popular-all-title" data-testid="popular-trips-all" className="px-4 pb-4 pt-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 id="popular-all-title" className="min-w-0 truncate text-[17px] font-semibold tracking-tight text-tp-ink">
          Popular road trips
        </h2>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-8 flex-none items-center gap-1 whitespace-nowrap rounded-lg px-2 text-[13px] font-medium text-tp-muted transition-colors hover:bg-tp-surface hover:text-tp-ink"
          >
            <ChevronGlyph dir="left" className="h-4 w-4" />
            {currentStopCount > 0 ? 'My trip' : 'Back'}
          </button>
        )}
      </div>
      {currentStopCount > 0 && (
        <p className="-mt-1 mb-3 flex items-start gap-1.5 text-[13px] leading-5 text-tp-muted">
          <InfoGlyph className="mt-0.5 h-4 w-4 flex-none text-tp-faint" />
          We ask before swapping out your trip.
        </p>
      )}
      <label htmlFor="popular-trip-filter" className="sr-only">
        Search popular trips
      </label>
      <div className="relative mb-3">
        <SearchGlyph className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-tp-faint" />
        <input
          id="popular-trip-filter"
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search, e.g. Perth to Esperance"
          className="h-11 w-full rounded-lg border-[1.5px] border-tp-line-strong pl-10 pr-3 text-[15px] text-tp-ink transition-colors placeholder:text-tp-faint hover:border-tp-line-strong focus:border-tp-brand focus:outline-none"
        />
      </div>
      {failed ? (
        errorState
      ) : shown === null ? (
        <div className="space-y-2.5" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex h-[108px] overflow-hidden rounded-xl border border-tp-line bg-white">
              <div className="w-[112px] flex-none animate-pulse bg-tp-line" />
              <div className="flex-1 space-y-2 p-3">
                <div className="h-3.5 w-3/4 animate-pulse rounded bg-tp-line" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-tp-surface" />
                <div className="h-8 w-28 animate-pulse rounded-lg bg-tp-surface" />
              </div>
            </div>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="py-8 text-center text-[13px] text-tp-muted">No trips match “{filter}”.</p>
      ) : (
        <ul className="space-y-2.5">
          {shown.map((r, i) => (
            <li key={r.id}>
              <TripRow route={r} loading={loadingId === r.id} disabled={busy} onTake={take} eager={i < 4} />
            </li>
          ))}
        </ul>
      )}
      {dialog}
    </section>
  );
}
